import { NgTemplateOutlet, TitleCasePipe, UpperCasePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  input,
  type InputSignal,
  output,
  type OutputEmitterRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, switchMap } from 'rxjs';

import {
  type ISubAccountAssessmentInfo,
  type ISubAccountLogicalStatusPayload,
  SELECTED_FOR_ASSESSMENT,
  SELECTED_FOR_ASSESSMENT_LABELS,
  type SelectedForAssessmentType,
  SUB_ACCOUNT_ACTIONS,
} from '@core/models/virtual-premium.model';
import { OVERSIGHT_STATUS, type OversightStatus } from '@shared/constants/ume-constants';
import { VpLogicalStatusDialogComponent } from '@shared/dialogs/vp-logical-status-dialog/vp-logical-status-dialog.component';
import { VirtualPremiumService } from '@shared/services/virtual-premium.service';

@Component({
  selector: 'ume-selected-for-assessment',
  templateUrl: './select-for-assessment.component.html',
  styleUrls: ['./select-for-assessment.component.scss'],
  imports: [
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule,
    MatDivider,
    NgTemplateOutlet,
    UpperCasePipe,
    TitleCasePipe,
  ],
})
export class SelectForAssessmentComponent {
  readonly selectedType = SELECTED_FOR_ASSESSMENT;
  readonly assessmentLabels = SELECTED_FOR_ASSESSMENT_LABELS;
  readonly status = OVERSIGHT_STATUS;

  static statusesForbiddenForEditing = new Set<OversightStatus>([
    OVERSIGHT_STATUS.ARCHIVED,
    OVERSIGHT_STATUS.UNDER_REVIEW_BY_UME,
    OVERSIGHT_STATUS.BLOCKED_BLOCKED,
    OVERSIGHT_STATUS.BLOCKED_DENIED,
  ] as OversightStatus[]);
  private virtualPremiumService = inject(VirtualPremiumService);
  private dialog = inject(MatDialog);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  public readonly brId: InputSignal<string> = input.required<string>();
  public readonly oversightStatus: InputSignal<OversightStatus | undefined> = input<
    OversightStatus | undefined
  >(undefined);
  public readonly subAccountCount: InputSignal<number> = input(0);
  public readonly assessmentInfo: InputSignal<ISubAccountAssessmentInfo> =
    input<ISubAccountAssessmentInfo>({} as ISubAccountAssessmentInfo);
  public readonly companyName: InputSignal<string> = input('');

  public readonly selectedForAssessmentChanged: OutputEmitterRef<SelectedForAssessmentType> =
    output<SelectedForAssessmentType>();
  public readonly updateSACount: OutputEmitterRef<{
    count: number;
    enableAssessment: boolean;
  }> = output<{
    count: number;
    enableAssessment: boolean;
  }>();

  public assessmentOptions: { value: SelectedForAssessmentType; label: string }[] = [
    { value: this.selectedType.NEW, label: this.assessmentLabels.NEW },
    {
      value: this.selectedType.SELECTED,
      label: this.assessmentLabels.SELECTED,
    },
    {
      value: this.selectedType.DISCARDED,
      label: this.assessmentLabels.DISCARDED,
    },
  ];

  private readonly _menuTrigger = viewChild(MatMenuTrigger);

  public listSelectionChange(value: SelectedForAssessmentType): void {
    this.selectedForAssessmentChanged.emit(value);

    this._menuTrigger()?.closeMenu();
  }

  public isStatusAvailableForEditing(oversightStatus: OversightStatus | undefined): boolean {
    return !SelectForAssessmentComponent.statusesForbiddenForEditing.has(
      oversightStatus as OversightStatus
    );
  }

  public get showSelectedCount(): boolean {
    return (
      this.assessmentInfo().selectedForAssessment === this.selectedType.SELECTED &&
      this.assessmentInfo().nroSelectedForAssessment !== this.subAccountCount() &&
      this.assessmentInfo().nroSelectedForAssessment !== 0
    );
  }

  public manageLogicalStatus(): void {
    let nroSubs = 0;

    this.dialog
      .open(VpLogicalStatusDialogComponent, {
        data: {
          brId: this.brId(),
          action: SUB_ACCOUNT_ACTIONS.LOGICAL_STATUS,
          companyName: this.companyName(),
          globalLogicalStatus: this.assessmentInfo().selectedForAssessment,
          subAccountCount: this.subAccountCount(),
        },
        width: '700px',
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((payload: ISubAccountLogicalStatusPayload[]) => {
          nroSubs = payload.filter((item) => item.isSelectedForAssessment).length;
          return this.virtualPremiumService.logicalStatusConfigure(this.brId(), payload);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this.updateSACount.emit({ count: nroSubs, enableAssessment: false });
      });
  }

  public getCorrectTooltipLabel(option: SelectedForAssessmentType): string {
    if (this.assessmentInfo().selectedForAssessment === option) {
      return `Counterparty is already marked as "${this.assessmentLabels[option]}"`;
    }

    if (option === this.selectedType.NEW && this.assessmentInfo().hasCurrentAssessment) {
      return `'New in the network' setting is not possible when an assessment was validated.`;
    } else if (
      option === this.selectedType.DISCARDED &&
      this.assessmentInfo().hasCurrentAssessment
    ) {
      return `'Unselect for assessment' is not possible when an assessment was validated.`;
    }

    return '';
  }
}
